import { css } from 'styled-components';

/**
 * Tokens do redesign do Portal Due It.
 *
 * Toda a identidade visual sai daqui: as telas consomem `var(--token)` e nunca
 * cores literais, o que faz o tema claro/escuro funcionar sem condicional
 * espalhada pelos componentes.
 *
 * As três camadas são, de baixo para cima:
 *   1. paleta da marca (--due-*), fixa nos dois temas;
 *   2. tokens de interface (--bg, --ink, --accent, --border...), que trocam
 *      de valor no escuro;
 *   3. aliases legados (--primary-color, --font-color...), mantidos porque as
 *      telas antigas já os usam — apontando para os tokens novos, elas herdam
 *      o redesign sem reescrita.
 */

const brand = css`
  /* Primárias (Manual de Marca, base logo Due Flow) */
  --due-azul: #009ee2;
  --due-roxo: #662482;
  --due-laranja: #eb7b27;
  --due-laranja-forte: #e84e1b;
  --due-rosa: #d60b51;

  /* Secundárias (tons de azul) */
  --due-azul-100: #6ed7ff;
  --due-azul-300: #20bdf9;
  --due-azul-500: #00adef;
  --due-azul-700: #2797c1;

  /* Bases / neutros */
  --due-cinza-900: #575756;
  --due-cinza-600: #8a8a89;
  --due-cinza-400: #c6c6c5;
  --due-cinza-200: #e2e0de;
  --due-cinza-100: #edecea;
  --due-cinza-050: #f5f4f3;
  --due-branco: #ffffff;

  /* Estados derivados */
  --due-azul-hover: #0089c4;
  --due-azul-press: #0074a6;
  --due-rosa-hover: #b90945;
`;

const lightInterface = css`
  --bg: #ffffff;
  --surface: #ffffff;
  --surface-2: var(--due-cinza-050);
  --surface-3: var(--due-cinza-100);

  --ink: var(--due-cinza-900);
  --ink-soft: #6e6d6c;
  --ink-faint: var(--due-cinza-600);
  --ink-on-accent: #ffffff;

  --border: var(--due-cinza-200);
  --border-strong: var(--due-cinza-400);

  --accent: var(--due-azul);
  --accent-deep: var(--due-azul-hover);
  --accent-press: var(--due-azul-press);
  --accent-tint: #e3f4fc;

  --roxo: var(--due-roxo);
  --roxo-tint: #efe6f3;
  --laranja: var(--due-laranja);
  --laranja-tint: #fcebdd;
  --rosa: var(--due-rosa);
  --rosa-tint: #fbe5ed;
  --success: #2e9e5b;
  --success-tint: #e4f5ec;
  --danger: var(--due-laranja-forte);
  --danger-tint: #fce7e1;

  --shadow-sm: 0 2px 8px rgba(87, 87, 86, 0.1);
  --shadow-md: 0 8px 24px rgba(87, 87, 86, 0.14);
  --shadow-lg: 0 18px 48px rgba(87, 87, 86, 0.2);
  --overlay: rgba(20, 18, 14, 0.4);
`;

const darkInterface = css`
  /* No escuro, --surface é igual a --bg de propósito: o rótulo dos campos
     pinta --surface por cima da borda do input para recortá-la, e qualquer
     diferença entre as duas cores vira um retângulo visível atrás do label.
     O custo é que card, modal e input deixam de se separar do fundo por
     preenchimento — passam a se separar só por --border. */
  --bg: #131214;
  --surface: #131214;
  --surface-2: #212023;
  --surface-3: #2a282c;

  --ink: #f1efea;
  --ink-soft: #b7b3ab;
  --ink-faint: #938f87;
  --ink-on-accent: #0b1a22;

  --border: #2c2a2c;
  --border-strong: #3a373a;

  --accent: #31c1ff;
  --accent-deep: #7adcff;
  --accent-press: #a5e8ff;
  --accent-tint: #0f2a38;

  --roxo: #c293d6;
  --roxo-tint: #241c2c;
  --laranja: #f0985a;
  --laranja-tint: #2e2118;
  --rosa: #f26fa0;
  --rosa-tint: #2e1721;
  --success: #57ce9f;
  --success-tint: #122a22;
  --danger: #ff8a63;
  --danger-tint: #33201a;

  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.5);
  --shadow-md: 0 12px 28px rgba(0, 0, 0, 0.5);
  --shadow-lg: 0 24px 60px rgba(0, 0, 0, 0.6);
  --overlay: rgba(0, 0, 0, 0.6);
`;

/* Aliases das telas antigas. Redirecionar aqui é o que propaga o redesign
   para páginas que ainda usam os nomes originais. */
const legacyAliases = css`
  --primary-color: var(--accent);
  --primary-font-color: var(--accent-deep);
  --accent-color: var(--laranja);
  --font-color: var(--ink);
`;

const scales = css`
  --font-sans: 'Avenir Next', 'Mulish', system-ui, -apple-system, 'Segoe UI',
    sans-serif;
  --font-display: var(--font-sans);

  --r-xs: 4px;
  --r-sm: 8px;
  --r-md: 20px;
  --r-lg: 28px;
  --r-pill: 999px;
  /* Raio das ações. Separado de --r-pill de propósito: a pílula fica para o
     que é rótulo (chip, badge, tag) e o botão vira retângulo arredondado, na
     mesma família do motivo de blocos da marca. */
  --r-btn: 10px;

  --header-h: 72px;
  --content-max: 1250px;

  --dur-fast: 0.15s;
  --dur-base: 0.24s;
  /* Volta completa de qualquer spinner: uma só duração para os indicadores não
     girarem em ritmos diferentes na mesma tela. */
  --dur-spin: 0.9s;
  /* Com "reduzir movimento" ligado o giro continua, mais lento — ver o mixin
     spinner, em mixins.js. */
  --dur-spin-reduced: 2s;
  --ease-standard: cubic-bezier(0.4, 0, 0.2, 1);
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
`;

const tokens = css`
  :root {
    ${brand}
    ${scales}
    ${lightInterface}
    ${legacyAliases}
    color-scheme: light;
  }

  /* Preferência do sistema enquanto o visitante não escolhe um tema.
     Depois do clique o data-theme abaixo assume nos dois sentidos. */
  @media (prefers-color-scheme: dark) {
    :root:not([data-theme='light']) {
      ${darkInterface}
      ${legacyAliases}
      color-scheme: dark;
    }
  }

  :root[data-theme='light'] {
    ${lightInterface}
    ${legacyAliases}
    color-scheme: light;
  }

  :root[data-theme='dark'] {
    ${darkInterface}
    ${legacyAliases}
    color-scheme: dark;
  }
`;

export default tokens;
